import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, Activity, Info, Loader2, Trash2, Filter,
} from 'lucide-react';
import { adminGet, adminPost } from '../data/api';
import t from '../i18n/it';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };

const LEVEL_CONFIG = {
  error: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10', label: t.admin.level_error },
  warning: { icon: Activity, color: 'text-amber-400', bg: 'bg-amber-500/10', label: t.admin.level_warning },
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10', label: t.admin.level_info },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t.notifications.just_now;
  if (mins < 60) return `${mins} ${t.notifications.minutes_ago}`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ${t.notifications.hours_ago}`;
  return `${Math.floor(hrs / 24)} ${t.notifications.days_ago}`;
}

export default function ErrorLogs() {
  const navigate = useNavigate();
  const [pin] = useState(() => sessionStorage.getItem('admin_pin') || '');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [clearing, setClearing] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!pin) { navigate('/admin'); return; }
    setLoading(true);
    try {
      const path = filter ? `/admin/error-logs?level=${filter}&limit=200` : '/admin/error-logs?limit=200';
      const data = await adminGet(path, pin);
      setLogs(data);
    } catch (e) {
      if (e.message === 'unauthorized') navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [pin, filter, navigate]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleClear = async () => {
    setClearing(true);
    try {
      await adminPost('/admin/clear-logs', pin);
      setLogs([]);
    } catch { /* handled */ } finally {
      setClearing(false);
    }
  };

  const filters = [
    { key: null, label: t.admin.filter_all },
    { key: 'error', label: t.admin.level_error },
    { key: 'warning', label: t.admin.level_warning },
    { key: 'info', label: t.admin.level_info },
  ];

  return (
    <div className="space-y-5 fade-in max-w-3xl" data-testid="error-logs-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            data-testid="back-to-admin"
            onClick={() => navigate('/admin')}
            className="w-8 h-8 rounded-sm flex items-center justify-center text-zinc-600 hover:text-zinc-300 transition-colors"
            style={{ border: '1px solid #27272A' }}
          >
            <ArrowLeft size={14} />
          </button>
          <div>
            <h1 className="text-4xl font-bold text-zinc-50 tracking-tight" style={heading}>{t.admin.error_logs}</h1>
            <p className="text-zinc-400 mt-1 text-sm">{t.admin.error_logs_subtitle}</p>
          </div>
        </div>
        <button
          data-testid="clear-logs-btn"
          onClick={handleClear}
          disabled={clearing || logs.length === 0}
          className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium text-red-400 hover:text-red-300 transition-colors disabled:opacity-30"
          style={{ border: '1px solid #3F1B1B' }}
        >
          {clearing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          {t.admin.clear_logs}
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Filter size={13} className="text-zinc-600" />
        {filters.map((f) => (
          <button
            key={f.label}
            data-testid={`filter-${f.key || 'all'}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-sm text-xs transition-colors ${
              filter === f.key
                ? 'bg-zinc-800 text-zinc-100'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
            style={filter === f.key ? { border: '1px solid #27272A' } : {}}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-zinc-600" style={mono}>{logs.length} log</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 justify-center py-16 text-zinc-500">
          <Loader2 size={18} className="animate-spin" /> {t.common.loading}
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-600">
          <Activity size={28} className="mb-3 text-zinc-700" />
          <span className="text-sm">{t.admin.no_logs}</span>
        </div>
      ) : (
        <div className="rounded-sm overflow-hidden" style={{ background: '#18181B', border: '1px solid #27272A' }}>
          {logs.map((log, i) => {
            const config = LEVEL_CONFIG[log.level] || LEVEL_CONFIG.info;
            const Icon = config.icon;
            return (
              <div
                key={i}
                data-testid={`log-entry-${i}`}
                className="flex items-start gap-3 px-5 py-3.5"
                style={i < logs.length - 1 ? { borderBottom: '1px solid #1F1F22' } : {}}
              >
                <div className={`w-6 h-6 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5 ${config.bg}`}>
                  <Icon size={12} className={config.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded ${config.bg} ${config.color}`}>
                      {config.label}
                    </span>
                    <span className="text-[10px] text-zinc-600 uppercase tracking-wider bg-zinc-800/50 px-1.5 py-0.5 rounded">
                      {log.source}
                    </span>
                    <span className="ml-auto text-[10px] text-zinc-600" style={mono}>{timeAgo(log.created_at)}</span>
                  </div>
                  <div className="text-sm text-zinc-300 leading-relaxed">{log.message}</div>
                  {log.details && (
                    <div className="text-xs text-zinc-600 mt-1" style={mono}>
                      {JSON.stringify(log.details)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
